import React, { FC, useState } from 'react'
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  Popover,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import { BodyText } from '@/components/atoms/BodyText'
import { MetaText } from '@/components/atoms/MetaText'
import { RADIUS } from '@/shared/styles/radii'

// trigger = secondary pill, popover = row click で即挿入 + auto close.
// MedicationPlaceholderText dialog と同じ「row click 即決」 pattern で統一.
// multi-select / footer button / header は引き算 (Notion 流: 装飾より動作の自明性).
const POPOVER_WIDTH = 260
const MAX_LIST_HEIGHT = 280

interface MedicationInsertButtonProps {
  medications: string[]
  onInsert: (text: string) => void
  disabled?: boolean
}

export const MedicationInsertButton: FC<MedicationInsertButtonProps> = ({
  medications,
  onInsert,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = (medication: string) => {
    onInsert(medication)
    handleClose()
  }

  const isEmpty = medications.length === 0

  return (
    <>
      <Button
        onClick={handleOpen}
        disabled={disabled || isEmpty}
        endIcon={
          <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'inherit' }} />
        }
        sx={{
          textTransform: 'none',
          height: 32,
          px: 1.75,
          borderRadius: `${RADIUS.full}px`,
          bgcolor: 'rgba(55,53,47,0.06)',
          color: 'text.primary',
          fontSize: 13,
          fontWeight: 500,
          gap: 0.5,
          minWidth: 0,
          '&:hover': { bgcolor: 'rgba(55,53,47,0.09)' },
          '&.Mui-disabled': {
            bgcolor: 'rgba(55,53,47,0.04)',
            color: 'text.disabled',
          },
        }}
      >
        + 医薬品名挿入
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: POPOVER_WIDTH,
              borderRadius: `${RADIUS.md}px`,
              boxShadow:
                '0 0 0 1px rgba(15,15,15,0.05), 0 3px 6px rgba(15,15,15,0.10), 0 9px 24px rgba(15,15,15,0.20)',
              mt: 0.5,
            },
          },
        }}
      >
        {isEmpty ? (
          <Box sx={{ px: 1.5, py: 1.5 }}>
            <MetaText sx={{ color: 'text.disabled' }}>
              処方薬情報がありません
            </MetaText>
          </Box>
        ) : (
          <List
            sx={{ maxHeight: MAX_LIST_HEIGHT, overflowY: 'auto', py: 0.5 }}
            dense
          >
            {medications.map((medication) => (
              <ListItem key={medication} disablePadding>
                <ListItemButton
                  onClick={() => handleSelect(medication)}
                  sx={{
                    height: 32,
                    px: 1,
                    mx: 0.5,
                    borderRadius: `${RADIUS.sm}px`,
                    '&:hover': { bgcolor: 'rgba(55,53,47,0.06)' },
                  }}
                >
                  <BodyText
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {medication}
                  </BodyText>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Popover>
    </>
  )
}
